import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Radio,
	RadioGroup,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { Air } from 'features/quotation/types';
import { calcularPrecioFinal } from 'features/salesOrder/components/SalesOrderServiceForm/helpers';
import { gdsOptions } from 'features/salesOrder/constants';
import { parseGDSAirRawDataSaleOrder } from 'features/salesOrder/services';
import { LoadedQuotation, WizardSavedPassenger, isFullQuotation } from '../../types';
import {
	FlightFareOverrides,
	buildFlightServiceDraft,
	flightFaresFromAir,
	mapQuotationLegsToFlightLegs,
} from '../../mappers/quotationServiceMappers';
import { getFlightPassengerCounts } from '../../helpers/flightPassengerCounts';
import { saveServiceToOrder } from '../../helpers/saveService';
import {
	formatFlightLimitDateForApi,
	isPnrFilled,
	suggestFlightLimitDateTimeLocal,
} from '../../helpers/flightServicePayload';
import { FlightItineraryPreview } from '../flights/FlightItineraryPreview';

interface Props {
	saleOrderId: string;
	quotation: LoadedQuotation;
	savedPassengers: WizardSavedPassenger[];
	onComplete: (skipped: boolean) => void;
	onSkip: () => void;
}

type FareField = keyof FlightFareOverrides;

const FARE_ROWS: { field: FareField; label: string; countKey: 'adult' | 'child' | 'infant' }[] = [
	{ field: 'fareADT', label: 'Adulto (ADT)', countKey: 'adult' },
	{ field: 'fareCHD', label: 'Menor (CHD)', countKey: 'child' },
	{ field: 'fareINF', label: 'Infante (INF)', countKey: 'infant' },
];

export const StepFlights = ({ saleOrderId, quotation, savedPassengers, onComplete, onSkip }: Props): JSX.Element => {
	const airs = useMemo(
		() => (isFullQuotation(quotation) || 'airs' in quotation ? quotation.airs || [] : []),
		[quotation],
	);
	const markup = quotation.markup ?? 0;
	const paxCounts = useMemo(() => getFlightPassengerCounts(quotation, savedPassengers), [quotation, savedPassengers]);

	const [selectedIndex, setSelectedIndex] = useState(0);
	const [fares, setFares] = useState<FlightFareOverrides>(() =>
		airs[0] ? flightFaresFromAir(airs[0] as Air) : { fareADT: 0, fareCHD: 0, fareINF: 0 },
	);
	const [limitDateTime, setLimitDateTime] = useState(() => suggestFlightLimitDateTimeLocal());
	const [currency, setCurrency] = useState('USD');
	const [pnr, setPnr] = useState('');
	const [pnrTouched, setPnrTouched] = useState(false);
	const [gds, setGds] = useState('n/a');
	const [gdsTouched, setGdsTouched] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const selectedAir: Air | undefined = airs[selectedIndex];

	useEffect(() => {
		const air = airs[selectedIndex];
		if (air) {
			setFares(flightFaresFromAir(air as Air));
			setLimitDateTime(suggestFlightLimitDateTimeLocal());
		}
	}, [selectedIndex, airs]);

	const pnrError = pnrTouched && !isPnrFilled(pnr);
	const gdsError = gdsTouched && (!gds || gds === 'n/a');
	const gdsChoices = gdsOptions.filter((opt) => opt.id !== 'n/a');

	const fareSummary = useMemo(() => {
		let totalCost = 0;
		let totalSale = 0;

		FARE_ROWS.forEach(({ field, countKey }) => {
			const count = paxCounts[countKey];
			if (count <= 0) return;
			const unitCost = fares[field] || 0;
			if (unitCost <= 0) return;
			const rowCost = unitCost * count;
			totalCost += rowCost;
			totalSale += parseFloat(calcularPrecioFinal(rowCost, markup));
		});

		return { totalCost, totalSale };
	}, [fares, markup, paxCounts]);

	if (airs.length === 0) {
		return (
			<Box>
				<Alert severity="info" sx={{ mb: 2 }}>
					Esta cotización no incluye vuelos. Podés agregarlos después en la orden.
				</Alert>
				<Box display="flex" justifyContent="flex-end">
					<Button variant="contained" onClick={() => onComplete(true)}>
						Continuar
					</Button>
				</Box>
			</Box>
		);
	}

	if (!selectedAir) {
		return null;
	}

	const handleFareChange = (field: FareField, raw: string) => {
		const parsed = parseFloat(raw.replace(',', '.'));
		setFares((prev) => ({
			...prev,
			[field]: Number.isFinite(parsed) ? parsed : 0,
		}));
	};

	const handleSave = async () => {
		setPnrTouched(true);
		setGdsTouched(true);

		if (!limitDateTime.trim()) {
			setError('Indicá la fecha límite del vuelo');
			return;
		}
		if (!isPnrFilled(pnr)) {
			setError('Completá el PNR de la reserva antes de guardar');
			return;
		}
		if (!gds || gds === 'n/a') {
			setError('Seleccioná el GDS del vuelo');
			return;
		}

		setLoading(true);
		setError(null);

		try {
			let flightLegs = selectedAir.data?.length ? mapQuotationLegsToFlightLegs(selectedAir.data) : [];

			if (flightLegs.length === 0 && selectedAir.tst?.trim()) {
				try {
					const tstResponse = await parseGDSAirRawDataSaleOrder(selectedAir.tst);
					flightLegs = tstResponse.data || [];
				} catch {
					// legs opcionales si falla TST
				}
			}

			const draft = buildFlightServiceDraft({
				air: selectedAir,
				quotation,
				savedPassengers,
				flightLegs,
				limitDate: formatFlightLimitDateForApi(limitDateTime),
				currency,
				reference: pnr.trim().toUpperCase(),
				fareOverrides: fares,
				gds,
			});

			await saveServiceToOrder(saleOrderId, draft as unknown as Record<string, unknown>);
			onComplete(false);
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Error al guardar el vuelo');
		} finally {
			setLoading(false);
		}
	};

	const visibleFareRows = FARE_ROWS.filter(({ countKey }) => paxCounts[countKey] > 0);

	return (
		<Box>
			<Typography variant="h6" fontWeight={700} gutterBottom>
				Vuelo
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Elegí la opción que confirmó el cliente, revisá el itinerario y ajustá costos si cambiaron respecto a la
				cotización.
			</Typography>

			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<FormControl component="fieldset" fullWidth>
					<RadioGroup value={selectedIndex} onChange={(e) => setSelectedIndex(Number(e.target.value))}>
						{airs.map((air, index) => (
							<FormControlLabel
								key={air.id ?? index}
								value={index}
								control={<Radio />}
								label={
									<Box>
										<Typography fontWeight={600}>
											Opción {index + 1} — {air.validatingCarrier}
											{air.data?.[0]?.flightNumber ? ` · ${air.data[0].flightNumber}` : ''}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											ADT ${air.fareADT} · CHD ${air.fareCHD}
											{air.fareINF > 0 ? ` · INF $${air.fareINF}` : ''}
										</Typography>
									</Box>
								}
							/>
						))}
					</RadioGroup>
				</FormControl>
			</Paper>

			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<FlightItineraryPreview air={selectedAir} />
			</Paper>

			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Typography variant="subtitle2" fontWeight={600} gutterBottom>
					Tarifas (costo unitario)
				</Typography>
				<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
					Margen de la cotización: {markup}% — la venta se calcula con la misma fórmula que en la orden.
				</Typography>

				{visibleFareRows.length === 0 ? (
					<Alert severity="warning">No hay pasajeros cargados para asignar tarifas por tipo.</Alert>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Tipo</TableCell>
								<TableCell align="center">Cant.</TableCell>
								<TableCell>Costo unit. ({currency})</TableCell>
								<TableCell align="right">Costo total</TableCell>
								<TableCell align="right">Venta total</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{visibleFareRows.map(({ field, label, countKey }) => {
								const count = paxCounts[countKey];
								const unitCost = fares[field] || 0;
								const rowCost = unitCost * count;
								const rowSale = unitCost > 0 ? parseFloat(calcularPrecioFinal(rowCost, markup)) : 0;

								return (
									<TableRow key={field}>
										<TableCell>{label}</TableCell>
										<TableCell align="center">{count}</TableCell>
										<TableCell>
											<TextField
												size="small"
												type="number"
												inputProps={{ min: 0, step: 0.01 }}
												value={unitCost}
												onChange={(e) => handleFareChange(field, e.target.value)}
												InputProps={{
													startAdornment: <InputAdornment position="start">$</InputAdornment>,
												}}
												sx={{ maxWidth: 140 }}
											/>
										</TableCell>
										<TableCell align="right">${rowCost.toFixed(2)}</TableCell>
										<TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
											${rowSale.toFixed(2)}
										</TableCell>
									</TableRow>
								);
							})}
							<TableRow>
								<TableCell colSpan={3}>
									<strong>Total servicio</strong>
								</TableCell>
								<TableCell align="right">
									<strong>${fareSummary.totalCost.toFixed(2)}</strong>
								</TableCell>
								<TableCell align="right">
									<strong>${fareSummary.totalSale.toFixed(2)}</strong>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				)}
			</Paper>

			<Paper
				variant="outlined"
				sx={{
					p: 2.5,
					mb: 2,
					borderWidth: 2,
					borderColor: pnrError || gdsError ? 'error.main' : 'primary.main',
					bgcolor: 'action.hover',
				}}
			>
				<Typography variant="subtitle1" fontWeight={700} gutterBottom color="primary.main">
					Reserva del vuelo
				</Typography>
				<Alert severity="info" sx={{ mb: 2 }}>
					Ingresá el <strong>PNR</strong> (localizador) y la <strong>fecha límite del vuelo</strong> para emitir o
					confirmar — son obligatorios para guardar el servicio.
				</Alert>

				<Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
					<TextField
						required
						fullWidth
						size="medium"
						label="PNR *"
						placeholder="Ej. K7X2M9"
						value={pnr}
						onChange={(e) => setPnr(e.target.value.toUpperCase())}
						onBlur={() => setPnrTouched(true)}
						error={pnrError}
						helperText={pnrError ? 'El PNR es obligatorio (no uses "-")' : 'Código de reserva / localizador del GDS'}
						inputProps={{ style: { textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 } }}
						sx={{ gridColumn: { md: '1 / -1' } }}
					/>
					<TextField
						required
						fullWidth
						size="small"
						type="datetime-local"
						label="Fecha límite del vuelo *"
						value={limitDateTime}
						onChange={(e) => setLimitDateTime(e.target.value)}
						InputLabelProps={{ shrink: true }}
						helperText="Fecha y hora tope para emisión (igual que en el servicio de vuelo de la orden)"
					/>
					<TextField size="small" label="Moneda" value={currency} onChange={(e) => setCurrency(e.target.value)} />
					<FormControl fullWidth size="small" required error={gdsError}>
						<InputLabel id="wizard-flight-gds-label">GDS *</InputLabel>
						<Select
							labelId="wizard-flight-gds-label"
							label="GDS *"
							value={gds}
							onChange={(e) => setGds(String(e.target.value))}
							onBlur={() => setGdsTouched(true)}
						>
							{gdsChoices.map((opt) => (
								<MenuItem key={String(opt.id)} value={String(opt.id)}>
									{opt.name}
								</MenuItem>
							))}
						</Select>
						{gdsError && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
								Seleccioná el GDS (Amadeus, Sabre, etc.)
							</Typography>
						)}
					</FormControl>
				</Box>
			</Paper>

			{error && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}

			<Box display="flex" justifyContent="space-between" mt={3}>
				<Button variant="text" onClick={onSkip} disabled={loading}>
					Omitir vuelo
				</Button>
				<Button variant="contained" color="success" onClick={() => void handleSave()} disabled={loading}>
					{loading ? <CircularProgress size={22} color="inherit" /> : 'Guardar vuelo y continuar'}
				</Button>
			</Box>
		</Box>
	);
};
